if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.viewer) return;
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const button = mediaToSwitch.querySelector('button');
        if (button) button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      this.handleGalleryResize = debounce(this.syncMobileGalleryHeight.bind(this), 100);
      window.addEventListener('resize', this.handleGalleryResize);
      if (this.mql.addEventListener) this.mql.addEventListener('change', this.handleGalleryResize);
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
      requestAnimationFrame(() => this.syncMobileGalleryHeight());
    }

    getMediaSlider() {
      return this.elements.viewer?.slider || this.elements.viewer?.querySelector('[id^="Slider-"]');
    }

    getActiveMedia() {
      return this.elements.viewer?.querySelector('[data-media-id].is-active') || this.elements.viewer?.querySelector('[data-media-id]');
    }

    getMediaFrame(mediaItem) {
      if (!mediaItem) return null;
      return mediaItem.querySelector('.product__modal-opener--image .product__media')
        || mediaItem.querySelector('.deferred-media')
        || mediaItem.querySelector('.product__media');
    }

    syncMobileGalleryHeight(mediaItem = this.getActiveMedia()) {
      const slider = this.getMediaSlider();
      if (!slider) return;

      if (this.mql.matches) {
        slider.style.removeProperty('--jf-active-media-height');
        return;
      }

      const frame = this.getMediaFrame(mediaItem);
      if (!frame) return;

      requestAnimationFrame(() => {
        const height = Math.ceil(frame.getBoundingClientRect().height);
        if (height > 0) slider.style.setProperty('--jf-active-media-height', `${height}px`);
      });
    }

    onSlideChanged(event) {
      const activeMedia = event.detail?.currentElement;
      const mediaId = activeMedia?.dataset?.mediaId;
      if (!mediaId) return;

      if (!this.mql.matches) {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });
        this.syncMobileGalleryHeight(activeMedia);
      }

      if (!this.elements.thumbnails) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(thumbnail);
    }

    scrollToItem(container, item) {
      if (!container || !item) return;
      if (document.documentElement.dir === 'rtl') {
        item.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
      } else {
        container.scrollTo({ left: item.offsetLeft });
      }
    }

    setActiveMedia(mediaId, prepend) {
      if (!this.elements.viewer || !mediaId) return;
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');
      this.syncMobileGalleryHeight(activeMedia);

      if (prepend) {
        activeMedia.parentElement?.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail?.parentElement) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          this.scrollToItem(activeMedia.parentElement, activeMedia);
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;
      const button = thumbnail.querySelector('button');
      if (!button) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      button.setAttribute('aria-current', true);
      if (typeof this.elements.thumbnails.isSlideVisible === 'function' && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.scrollToItem(this.elements.thumbnails.slider, thumbnail);
    }

    announceLiveRegion(activeItem, position) {
      if (!this.elements.liveRegion || !position) return;
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media, product-model');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer?.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems?.forEach((slide) => slide.setAttribute('role', 'presentation'));
    }
  });
}
